import type { NamePath } from 'antd/lib/form/interface';
import { type ComponentProps, type RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadImage } from 'features/files';
import {
  type RichTextData,
  RichTextEditor,
  type RichTextEditorBase,
  type RichTextMentions,
} from '../../rich-text-editor';
import { EmptyInput, type EmptyInputContentProperties } from '../empty-input';
import { Item } from '../item';

type EditorProperties = {
  editorRef?: RefObject<null | RichTextEditorBase>;
  height?: ComponentProps<typeof RichTextEditor>['height'];
  id?: string;
  isFocusedInitially?: boolean;
  label?: string;
  maxHeight?: ComponentProps<typeof RichTextEditor>['maxHeight'];
  mentions?: RichTextMentions;
  minHeight?: ComponentProps<typeof RichTextEditor>['minHeight'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  onChange?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  onChangeText?: any;
  onUploading?: (uploading: boolean) => void;
  placeholder?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  value?: any;
};

type ItemProperties = {
  'data-test-id'?: string;
  editorRef?: RefObject<null | RichTextEditorBase>;
  height?: ComponentProps<typeof RichTextEditor>['height'];
  isFocusedInitially?: boolean;
  label?: string;
  maxHeight?: ComponentProps<typeof RichTextEditor>['maxHeight'];
  mentions?: RichTextMentions;
  minHeight?: ComponentProps<typeof RichTextEditor>['minHeight'];
  placeholder?: string;
} & Omit<React.ComponentProps<typeof Item>, 'children' | 'label'>;

type RichTextInputProperties = {
  html: ItemProperties;
  text: EmptyInputContentProperties<string>;
};

const Editor = ({
  editorRef,
  height,
  id,
  isFocusedInitially,
  label,
  maxHeight,
  mentions,
  minHeight,
  onChange,
  onChangeText,
  onUploading,
  placeholder,
  value,
}: EditorProperties) => {
  const { t } = useTranslation();

  const handleChange = (newValue: RichTextData) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    onChange(newValue.html);

    if (onChangeText) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      onChangeText(newValue.text);
    }
  };

  // TODO: onClick from `label` is not focusing to RTE input
  return (
    <>
      {label ? (
        <div className="ant-col ant-form-item-label">
          <label id={id}>{t(label)}</label>
        </div>
      ) : null}
      <div className="rich-text-input__border">
        <RichTextEditor
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          data={value}
          editorRef={editorRef}
          height={height}
          isFocusedInitially={isFocusedInitially}
          labelId={id}
          maxHeight={maxHeight}
          mentions={mentions}
          minHeight={minHeight}
          onChange={handleChange}
          onRequestUpload={uploadImage}
          onUploading={onUploading}
          placeholder={placeholder ?? 'Description'}
        />
      </div>
    </>
  );
};

const RichTextInput = ({
  html: {
    editorRef,
    height,
    isFocusedInitially,
    label,
    maxHeight,
    mentions,
    minHeight,
    placeholder,
    rules = [],
    ...htmlRest
  },
  text,
}: RichTextInputProperties) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const [oldTextName, setOldTextName] = useState<NamePath>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const [onChangeText, setOnChangeText] = useState<(value: any) => void>();
  const [isUploading, setIsUploading] = useState(false);

  const onUploading = (uploading: boolean) => {
    setIsUploading(uploading);
  };

  const isSameTextName = () => {
    if (Array.isArray(oldTextName) && Array.isArray(text.name) && oldTextName.length === text.name.length) {
      // TODO: Fix type
      const fixedTypeAlias = text.name as unknown[];
      return oldTextName.every((item, index) => item === fixedTypeAlias[index]);
    }

    return oldTextName === text.name;
  };

  // TODO: Fix issue with Form implementation causing potential errors thrown in RTE.
  return (
    <>
      <EmptyInput<string>
        {...text}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        onSetOnChange={(value: any) => {
          if (!onChangeText || !isSameTextName()) {
            setOldTextName(text.name);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            setOnChangeText(() => value);
          }
        }}
      />
      <Item
        {...htmlRest}
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        data-test-id={htmlRest?.['data-test-id']}
        hideLabel
        label={label}
        rules={[
          ...rules,
          {
            // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
            async validator(_rule, _value) {
              if (isUploading) {
                throw new Error(t('Please wait for image upload'));
              }
            },
          },
        ]}
        style={{ flexWrap: 'nowrap' }}
      >
        <Editor
          editorRef={editorRef}
          height={height}
          isFocusedInitially={isFocusedInitially}
          label={label}
          maxHeight={maxHeight}
          mentions={mentions}
          minHeight={minHeight}
          onChangeText={onChangeText}
          onUploading={onUploading}
          placeholder={placeholder ? t(placeholder) : placeholder}
        />
      </Item>
    </>
  );
};

export default RichTextInput;
